import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

const PageHeader = ({ eyebrow, title, titleItalic, intro, accent = "var(--st-gold)" }) => (
    <section data-testid="page-header" className="bg-[var(--st-cream)] pt-36 lg:pt-44 pb-20 lg:pb-28">
        <div className="st-container">
            <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="eyebrow mb-6"
                style={{ color: accent }}
            >
                {eyebrow}
            </motion.p>
            <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.1 }}
                className="font-serif leading-[0.9] tracking-tighter max-w-5xl"
                style={{ fontSize: "clamp(3rem, 7.5vw, 7.5rem)" }}
            >
                {title} {titleItalic && <span className="italic" style={{ color: accent }}>{titleItalic}</span>}
            </motion.h1>
            {intro && (
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.9, delay: 0.3 }}
                    className="mt-10 text-lg lg:text-xl leading-relaxed max-w-3xl text-[var(--st-ink-soft)]"
                >
                    {intro}
                </motion.p>
            )}
        </div>
    </section>
);

const ServiceList = ({ title, items, color }) => (
    <div className="border-t border-[var(--st-line)] py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
                <div className="w-8 h-8 mb-4" style={{ background: color }} />
                <h3 className="font-serif text-4xl lg:text-5xl" style={{ color }}>{title}</h3>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
                <ul className="divide-y divide-[var(--st-line)]">
                    {items.map((it) => (
                        <li key={it.title} className="py-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <p className="font-serif text-2xl">{it.title}</p>
                            <p className="md:col-span-2 text-[var(--st-ink-soft)] leading-relaxed">{it.body}</p>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    </div>
);

export const Design = () => (
    <main data-testid="design-page">
        <PageHeader
            eyebrow="Design · The making room"
            title="Design solutions for founders"
            titleItalic="and startups, built to last."
            intro="The studio's making room. Brand identities, websites, packaging, campaigns and physical environments — built to feel like one continuous breath, from first sketch to shelf."
            accent="var(--st-green)"
        />
        <section className="bg-[var(--st-cream-soft)] py-20 lg:py-28">
            <div className="st-container">
                <ServiceList
                    title="Brand Identity"
                    color="var(--st-green)"
                    items={[
                        { title: "Naming", body: "Names that hold up after the launch dust settles." },
                        { title: "Logo & wordmark", body: "Marks built to age well across decades, not algorithms." },
                        { title: "Visual systems", body: "Type, colour, grids and motion principles. The full grammar." },
                        { title: "Brand guidelines", body: "Documents teams actually open and use, not 200-page PDFs." },
                    ]}
                />
                <ServiceList
                    title="Digital Marketing"
                    color="var(--st-green)"
                    items={[
                        { title: "Campaign design", body: "Hero campaigns that move the needle on awareness and consideration." },
                        { title: "Social media systems", body: "Visual content engines designed for sustainability, not sprint." },
                        { title: "Email design", body: "Newsletters readers look forward to. (Yes, that's still possible.)" },
                    ]}
                />
                <ServiceList
                    title="UI / UX & Web"
                    color="var(--st-green)"
                    items={[
                        { title: "Website design", body: "Editorial-grade websites for considered brands and founders." },
                        { title: "E-commerce", body: "Shopify storefronts engineered around storytelling, not just SKUs." },
                        { title: "Product UX", body: "Interaction design for digital products and onboarding flows." },
                    ]}
                />
                <ServiceList
                    title="Events & Spaces"
                    color="var(--st-green)"
                    items={[
                        { title: "Spatial branding", body: "Wayfinding, signage and environmental graphics for retail and workspaces." },
                        { title: "Event design", body: "Launches, salons and intimate gatherings — choreographed end to end." },
                    ]}
                />
                <div className="pt-16 text-center">
                    <Link to="/contact" data-testid="design-cta" className="btn-primary">
                        Tell us about your project <ArrowUpRight size={16} />
                    </Link>
                </div>
            </div>
        </section>
    </main>
);

export const Consult = () => (
    <main data-testid="consult-page">
        <PageHeader
            eyebrow="Consult · The thinking room"
            title="Brand management for founders"
            titleItalic="who want to be seen."
            intro="Sometimes the answer isn't a new logo — it's a clearer point of view. We work as your brand management partner to promote, strategise, and market for leads or visibility, not just a vendor for a one-off deliverable."
            accent="var(--st-coral)"
        />
        <section className="bg-[var(--st-cream-soft)] py-20 lg:py-28">
            <div className="st-container">
                <ServiceList
                    title="Brand Audits"
                    color="var(--st-coral)"
                    items={[
                        { title: "Visual audit", body: "Every touchpoint, mapped and graded against your brand promise." },
                        { title: "Verbal audit", body: "Voice, tone and messaging consistency across customer journeys." },
                        { title: "Competitive landscape", body: "Where you stand, who you're losing to, and the white space nobody's seen." },
                    ]}
                />
                <ServiceList
                    title="Market Positioning"
                    color="var(--st-coral)"
                    items={[
                        { title: "Positioning workshops", body: "Half-day sessions to find the words that finally fit." },
                        { title: "Audience definition", body: "Who you're really for, not who you wish you were for." },
                        { title: "Narrative architecture", body: "The story that connects strategy to the website headline." },
                    ]}
                />
                <ServiceList
                    title="Go-to-Market"
                    color="var(--st-coral)"
                    items={[
                        { title: "Launch strategy", body: "Sequenced rollouts for new brands, products and category entries." },
                        { title: "Channel mix", body: "Where to show up first, where to come back to later, and what to skip." },
                    ]}
                />
                <ServiceList
                    title="Startup Mentorship"
                    color="var(--st-coral)"
                    items={[
                        { title: "Founder retainer", body: "Monthly strategic sessions with Sugandhi for early-stage founders." },
                        { title: "Brand sprint", body: "Two intensive weeks, end-to-end brand foundation for new ventures." },
                    ]}
                />
                <div className="pt-16 text-center">
                    <Link to="/contact" data-testid="consult-cta" className="btn-primary">
                        Book a consult <ArrowUpRight size={16} />
                    </Link>
                </div>
            </div>
        </section>
    </main>
);

const TRACKS = [
    {
        title: "Visual Communication",
        body: "Design thinking, typography, colour and layout — the visual grammar behind every brand you remember.",
    },
    {
        title: "Brand Marketing",
        body: "Positioning, storytelling and go-to-market — how a brand earns a place in someone's mind.",
    },
    {
        title: "Social Media Marketing",
        body: "Content systems, platform strategy and community — showing up with intention, week after week.",
    },
];

const TrackCard = ({ title, body, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        className="border border-[var(--st-line)] p-8 lg:p-10"
    >
        <p className="eyebrow mb-4" style={{ color: "var(--st-purple)" }}>Track 0{index + 1}</p>
        <h3 className="font-serif text-3xl mb-4">{title}</h3>
        <p className="text-[var(--st-ink-soft)] leading-relaxed">{body}</p>
    </motion.div>
);

export const Learn = () => (
    <main data-testid="learn-page">
        <PageHeader
            eyebrow="Learn · The teaching room"
            title="Three tracks, fifteen years"
            titleItalic="of practice, distilled and shared."
            intro="Learn Visual Communication, Brand Marketing, or Social Media Marketing — taught through workshops, mentorship cohorts and an online course, the way we actually practice it in-studio."
            accent="var(--st-purple)"
        />
        <section className="bg-[var(--st-cream)] pb-8 lg:pb-12">
            <div className="st-container">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {TRACKS.map((t, i) => (
                        <TrackCard key={t.title} index={i} {...t} />
                    ))}
                </div>
            </div>
        </section>
        <section className="bg-[var(--st-cream-soft)] py-20 lg:py-28">
            <div className="st-container">
                <ServiceList
                    title="Workshops"
                    color="var(--st-purple)"
                    items={[
                        { title: "Brand-thinking for founders", body: "Half-day intensive on positioning, naming and visual systems. Gurgaon, KL, online." },
                        { title: "Designing for trust", body: "For early-stage teams who need to look credible without looking corporate." },
                        { title: "Inside the studio", body: "A behind-the-scenes look at how a real branding project runs." },
                    ]}
                />
                <ServiceList
                    title="1:1 Mentorship"
                    color="var(--st-purple)"
                    items={[
                        { title: "Designer mentorship", body: "Six-week pods for early-career designers wanting studio-level practice." },
                        { title: "Founder mentorship", body: "Monthly sessions with Sugandhi for women founders navigating brand decisions." },
                    ]}
                />
                <ServiceList
                    title="Online Course"
                    color="var(--st-purple)"
                    items={[
                        { title: "Build a brand that gets remembered", body: "A self-paced course of nine modules, with feedback rounds. Cohort begins quarterly." },
                    ]}
                />
                <ServiceList
                    title="Community"
                    color="var(--st-purple)"
                    items={[
                        { title: "Focused Female Founders", body: "Sugandhi's WhatsApp group for women building businesses with intention. Free to join." },
                    ]}
                />
                <div className="pt-16 text-center">
                    <Link to="/contact" data-testid="learn-cta" className="btn-primary">
                        Join the next cohort <ArrowUpRight size={16} />
                    </Link>
                </div>
            </div>
        </section>
    </main>
);
